import { useMemo, useState } from 'react';
import { Input, Label, Message, Slider } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { TCalculatorProducts, TSupportedLocales } from 'types';
import InputFormatter from 'utils/inputFormatters/inputFormatters';
import {
  MAX_DE_CREDIT_FACILITY_INTEREST_RATE,
  MAX_DE_TERM_LOAN_INTEREST_RATE,
  MAX_UK_CREDIT_FACILITY_INTEREST_RATE,
  MAX_UK_FLEXI_24_INTEREST_RATE,
  MAX_UK_TERM_LOAN_INTEREST_RATE,
  MIN_DE_INTEREST_RATE,
  MIN_UK_INTEREST_RATE,
} from '../filterConfigs.helper';
import standAloneCalculatorStyles from '../StandAloneCalculator.module.css';
import styles from './InterestRateFilter.module.css';

type TProps = {
  product: TCalculatorProducts;
  value: string;
  maxTermLength: number;
  onChange: (interestRate: string) => void;
};

export const getInterestConfig = (
  locale: TSupportedLocales,
  maxTermLength: number,
  product: TCalculatorProducts,
) => {
  if (locale === 'de') {
    return {
      min: Number(MIN_DE_INTEREST_RATE),
      max: Number(
        maxTermLength === 60
          ? MAX_DE_TERM_LOAN_INTEREST_RATE
          : MAX_DE_CREDIT_FACILITY_INTEREST_RATE,
      ),
    };
  }

  return {
    min: Number(MIN_UK_INTEREST_RATE),
    max:
      product === 'flexi24'
        ? Number(MAX_UK_FLEXI_24_INTEREST_RATE)
        : maxTermLength === 60
          ? Number(MAX_UK_TERM_LOAN_INTEREST_RATE)
          : Number(MAX_UK_CREDIT_FACILITY_INTEREST_RATE),
  };
};

export const InterestRateFilter = ({
  product,
  value,
  maxTermLength,
  onChange,
}: TProps) => {
  const [error, setError] = useState<string | null>(null);
  const { formatPercentage } = useNumberFormat();
  const { locale, isDE } = useLocale();

  const interestRate = getInterestConfig(locale, maxTermLength, product);

  const normalizedValue = useMemo(() => value.replace(',', '.'), [value]);

  const handleBlur = () => {
    setError(null);
    if (Number(normalizedValue) > interestRate.max) {
      onChange(interestRate.max.toLocaleString(locale));
    } else if (Number(normalizedValue) < interestRate.min) {
      onChange(interestRate.min.toLocaleString(locale));
    }
  };

  const handleNumberChange = (value: string) => {
    const normalizedValue = value.replace(',', '.');
    setError(null);
    if (Number(normalizedValue) < interestRate.min) {
      setError(t`Please enter a minimum of ${interestRate.min}`);
    } else if (Number(normalizedValue) > interestRate.max) {
      setError(t`Please enter a maximum of ${interestRate.max}`);
    }

    onChange(InputFormatter.Float(value, isDE));
  };

  return (
    <div className={styles.container}>
      <div className={styles.inputContainer}>
        <Label htmlFor="interest-rate" className={styles.label}>
          <Trans>Interest rate</Trans>
        </Label>
        <Input
          id="interest-rate"
          className={styles.input}
          value={value}
          onChange={(e) => handleNumberChange(e.target.value)}
          onBlur={handleBlur}
        />
      </div>
      <Slider
        data-testid="interest-rate-slider"
        labelClassName={standAloneCalculatorStyles['label']}
        value={normalizedValue}
        step={0.05}
        leftLabel={formatPercentage(interestRate.min / 100)}
        rightLabel={formatPercentage(interestRate.max / 100)}
        min={interestRate.min}
        max={interestRate.max}
        onChange={(e) => handleNumberChange(e.target.value)}
      />
      {error && (
        <Message
          className={styles.error}
          size="small"
          type="error"
          message={error}
        />
      )}
    </div>
  );
};
